/**
 * S6 („Keine Sackgassen“, 04.11.2026): Sperren und fachliche Ablehnungen (HTTP 409 und ein paar bekannte 422-Codes) stehen
 * an der Aktion, die gescheitert ist, nicht als Sammelkasten oben auf der Seite: ein Satz „Geht gerade nicht: …“ plus ein
 * Knopf mit dem Ausweg. Diese Datei ist die EINE Zuordnung Fehlercode -> Satz + Ausweg; `ui/SperreVorOrt.tsx` zeigt sie.
 *
 * - Echte Serverfehler (5xx), Netzfehler und alles ohne Statuscode sind KEINE Sperre (`sperreAusFehler` -> null): die bleiben laut.
 * - Unbekannter Code bei 409: Satz aus der Server-Meldung, Ausweg „Neu laden“.
 * - Liefert der Server künftig ein Feld `aktion` mit einem der Ausweg-Namen unten, gewinnt es vor der Zuordnung hier.
 */
import type { ApiError } from "./api";

export type SperrLageAusweg =
  | "neu_laden"
  | "fassung_aktualisieren"
  | "neu_senden"
  | "nachtrag_erstellen"
  | "gueltigkeit_verlaengern"
  | "termin_aendern"
  | "zur_abrechnung";

export type SperrLage = {
  /** Maschinenlesbarer Fehlercode des Servers (nur zur Weitergabe/Tests, nie anzeigen). */
  code?: string;
  /** Der Grund in einem deutschen Satz, ohne Vorspann. */
  grund: string;
  ausweg: SperrLageAusweg;
};

export const AUSWEG_LABEL: Record<SperrLageAusweg, string> = {
  neu_laden: "Neu laden",
  fassung_aktualisieren: "Fassung aktualisieren",
  neu_senden: "Jetzt neu senden",
  nachtrag_erstellen: "Nachtrag erstellen",
  gueltigkeit_verlaengern: "Gültigkeit verlängern",
  termin_aendern: "Termin ändern",
  zur_abrechnung: "Zur Abrechnung",
};

export const AUSWEG_ICON: Record<SperrLageAusweg, string> = {
  neu_laden: "refresh",
  fassung_aktualisieren: "sync",
  neu_senden: "send",
  nachtrag_erstellen: "edit_document",
  gueltigkeit_verlaengern: "event_repeat",
  termin_aendern: "edit_calendar",
  zur_abrechnung: "receipt_long",
};

export const SPERRE_VORSPANN = "Geht gerade nicht:";

/** `satz`: fester deutscher Grund (sonst die Server-Meldung); `ausweg`: der Knopf. */
const ZUORDNUNG: Record<string, { satz?: string; ausweg: SperrLageAusweg }> = {
  fassung_wird_aktualisiert: { satz: "Die gesendete Fassung wird gerade aktualisiert. Bis zum Neuversand kann der Gast nicht annehmen.", ausweg: "neu_senden" },
  event_locked: { satz: "Das Angebot ist verbindlich angenommen. Änderungen gehen nur als Nachtrag mit neuer Zustimmung.", ausweg: "nachtrag_erstellen" },
  a_la_carte_nach_versand: { satz: "Das Kennzeichen „À la carte“ lässt sich nach dem Versand nicht mehr ändern.", ausweg: "neu_laden" },
  event_cancelled: { satz: "Der Vorgang ist abgesagt.", ausweg: "neu_laden" },
  contract_in_progress: { satz: "Für diesen Vorgang wird gerade eine Kostenübernahme erstellt. Kurz warten, dann noch einmal.", ausweg: "neu_laden" },
  conflict: { satz: "Der Vorgang wurde zwischenzeitlich geändert.", ausweg: "fassung_aktualisieren" },
  stand_veraltet: { satz: "Der Stand wurde zwischenzeitlich geändert.", ausweg: "fassung_aktualisieren" },
  cross_option: { satz: "Eine Position gehört zu einer anderen Variante.", ausweg: "fassung_aktualisieren" },
  fassung_nicht_festgehalten: { satz: "Die aktuelle Fassung konnte nicht festgehalten werden.", ausweg: "fassung_aktualisieren" },
  invalid_status: { satz: "Der Vorgang hat inzwischen einen anderen Status.", ausweg: "neu_laden" },
  // Der Server nennt den Ist-Status mit deutschem Namen („Der Vorgang steht inzwischen auf „Storniert“.“): diese Meldung gewinnt.
  invalid_transition: { ausweg: "neu_laden" },
  not_resendable: { satz: "Zu diesem Stand gibt es keine Angebotsversion, die sich erneut senden lässt.", ausweg: "neu_laden" },
  ausgleich_offen: { ausweg: "zur_abrechnung" },
  abrechnung_abgeschlossen: { ausweg: "zur_abrechnung" },
  offer_expired: { satz: "Die Gültigkeit des Angebots ist abgelaufen.", ausweg: "gueltigkeit_verlaengern" },
  angebot_abgelaufen: { satz: "Die Gültigkeit des Angebots ist abgelaufen.", ausweg: "gueltigkeit_verlaengern" },
  // #1895: Erinnerung/Zusage bei abgelaufener Gültigkeit (409). Der Server-Satz nennt das Datum und bleibt stehen.
  gueltigkeit_abgelaufen: { ausweg: "gueltigkeit_verlaengern" },
  // #1895: Veranstaltungstag liegt in der Vergangenheit (409) -> Termin ändern (ohne Termin-Handler: Neu laden).
  veranstaltung_vergangen: { ausweg: "termin_aendern" },
};

/** Codes unterhalb von 409, die trotzdem erwartbare Sperren sind (kein „Etwas ist schiefgelaufen“). */
const AUCH_BEI_422 = new Set(["offer_expired", "angebot_abgelaufen"]);

function istAusweg(x: unknown): x is SperrLageAusweg {
  return typeof x === "string" && x in AUSWEG_LABEL;
}

function alsApiError(err: unknown): ApiError | null {
  if (!err || typeof err !== "object") return null;
  const code = (err as { statusCode?: unknown }).statusCode;
  return typeof code === "number" ? (err as ApiError) : null;
}

/** Ist das eine erwartbare Sperre (409 bzw. bekannter 422-Code)? Dann Satz + Ausweg, sonst `null` (Fehler bleibt laut). */
export function sperreAusFehler(err: unknown): SperrLage | null {
  const e = alsApiError(err);
  if (!e) return null;
  const bekannt = e.code ? ZUORDNUNG[e.code] : undefined;
  const ist409 = e.statusCode === 409;
  const ist422Sperre = e.statusCode === 422 && !!e.code && AUCH_BEI_422.has(e.code);
  if (!ist409 && !ist422Sperre) return null;

  const aktion = (e.body as { aktion?: unknown } | undefined)?.aktion;
  const ausweg: SperrLageAusweg = istAusweg(aktion) ? aktion : (bekannt?.ausweg ?? "neu_laden");
  const roh = (e.message ?? "").trim();
  // Ein nackter Code oder die HTTP-Statuszeile ist kein Satz.
  const brauchbar = roh && roh !== e.code && !/^(conflict|http \d+)$/i.test(roh) && roh.length > 12 ? roh : "";
  // „Bitte neu laden.“ am Ende der Server-Meldung sagt der Knopf schon.
  const ohneNeuLaden = brauchbar.replace(/\s*Bitte (die Seite )?neu laden\.?$/i, "").trim();
  const grund = bekannt?.satz ?? ((ausweg === "neu_laden" || ausweg === "fassung_aktualisieren" ? ohneNeuLaden : brauchbar) || "Der Stand hat sich geändert.");
  return { code: e.code, grund, ausweg };
}

/** Der volle Satz, so wie er im Hinweis steht (auch für Tests und Hilfetexte). */
export function sperreSatz(s: SperrLage): string {
  return `${SPERRE_VORSPANN} ${s.grund}`;
}
